import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { IResortTopRated, ResortTopRated } from '@/api/home/types';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { RegularAd } from '@/tracking/ads/components/RegularAd';
import { isValidResponse } from '@/util/isValidResponse';
import { fetchResortTopRating } from '@/api/home/static';
import { fetchRegionResortTopRating } from '@/api/region/static';
import { Diagnostics } from '@/util/diagnostics';
import { ItemsContainer } from '@/components/common/ItemsContainer';
import { ResortRankCard } from '@/components/common/ResortRankCard';
import { Skeleton } from '@/components/ui/skeleton';
import { Routes } from '@/routes';

const DEBUG_TAG_HOME: string = 'homepage-best-resorts';

type Slot =
  | { kind: 'resort'; key: string; rank: number; item: IResortTopRated }
  | { kind: 'ad'; key: string };

const GRID_VISIBLE_LIMIT = 6;

export const HomeBestResorts = ({
  resortTopRatingOverall,
  activeTab,
  regionUUID,
  pageType,
  displayMode = 'carousel',
  headerLeft,
}: {
  resortTopRatingOverall: Array<IResortTopRated>,
  activeTab: ResortTopRated,
  regionUUID?: string,
  pageType?: 'global' | undefined,
  isUsedInHomePage?: boolean,
  displayMode?: 'carousel' | 'grid',
  headerLeft?: React.ReactNode,
}) => {
  const { t } = useOTSTranslation('common');
  const [isLoading, toggleLoading] = useState<boolean>(false);
  const [resortTopRating, setResortTopRating] = useState<Array<IResortTopRated>>(resortTopRatingOverall);

  useEffect(() => {
    setResortTopRating(resortTopRatingOverall);
  }, [resortTopRatingOverall]);

  const checkValidResponse = (response: Array<IResortTopRated>) => (
    response.every((item) => isValidResponse(item, ['resort', 'resortPath']))
  );

  const loadNewResortList = useCallback(async () => {
    toggleLoading(true);
    const response = regionUUID ? (
      await fetchRegionResortTopRating(regionUUID, activeTab)
    ) : (
      await fetchResortTopRating(activeTab)
    );

    if (!checkValidResponse(response)) {
      Diagnostics.message({ message: 'Invalid data received', tag: DEBUG_TAG_HOME });
      return;
    }

    setResortTopRating(response);
    toggleLoading(false);
  }, [activeTab, setResortTopRating, regionUUID]);

  useEffect(() => {
    loadNewResortList();
  }, [activeTab]);

  // Ad insertion: after index 2 on home (one slot in), after indexes
  // 5 and 11 on region (mid-list and near the tail). Same cadence the
  // production layout used — expressed as carousel items mid-track.
  // Grid mode trims to the first 6 resorts and skips ad insertion so
  // the 2x3 grid stays clean.
  const slots = useMemo<Slot[]>(() => {
    const out: Slot[] = [];
    const source = displayMode === 'grid'
      ? resortTopRating.slice(0, GRID_VISIBLE_LIMIT)
      : resortTopRating;
    source.forEach((item, index) => {
      out.push({
        kind: 'resort',
        key: `resort-${item.resort.uuid}`,
        rank: index + 1,
        item,
      });
      if (displayMode === 'grid') return;
      const isHomeAdSlot = pageType !== 'global' && !regionUUID && index === 2;
      const isRegionAdSlot = pageType !== 'global' && regionUUID && (index === 5 || index === 11);
      if (isHomeAdSlot || isRegionAdSlot) {
        out.push({ kind: 'ad', key: `ad-${index}` });
      }
    });
    return out;
  }, [resortTopRating, pageType, regionUUID, displayMode]);

  return (
    <ItemsContainer
      // Keying on activeTab forces a remount of the scroll rail when the
      // user picks a different chip. Without this, the rail keeps its
      // previous scrollLeft and the new list looks like it starts at
      // wherever the user was peeking before.
      key={activeTab}
      ariaLabel={t('common.topRatedResorts')}
      desktopLayout={displayMode === 'grid' ? 'grid' : 'carousel'}
      // Carousel rail shows 5 square tiles at lg+. Grid mode keeps the
      // legacy 3-up layout — the source list is short enough that 5
      // wide would leave a half-full bottom row.
      desktopColumns={displayMode === 'grid' ? 3 : 5}
      headerLeft={headerLeft}
    >
      {isLoading
        ? slots.map((slot) => (
          <ItemsContainer.Item key={slot.key}>
            {slot.kind === 'ad'
              ? <RegularAd className="w-full" wrapperClassName="w-full" />
              : <ResortRankCardSkeleton />}
          </ItemsContainer.Item>
        ))
        : slots.map((slot) => {
          if (slot.kind === 'ad') {
            return (
              <ItemsContainer.Item key={slot.key}>
                <RegularAd className="w-full" wrapperClassName="w-full" />
              </ItemsContainer.Item>
            );
          }
          return (
            <ItemsContainer.Item key={slot.key}>
              <ResortRankCard
                rank={slot.rank}
                title={slot.item.resort.title}
                subtitle={slot.item.resortPath.region_en}
                image={slot.item.resort.image || slot.item.resort.smallImage}
                href={Routes.resortTripPlanningOverview(slot.item.resortPath)}
              />
            </ItemsContainer.Item>
          );
        })}
    </ItemsContainer>
  );
};

// Layout-matching placeholder for ResortRankCard: square image area with
// a rank badge stub top-right and a name stub overlaid bottom-left.
const ResortRankCardSkeleton = () => (
  <div className="block">
    <div className="relative aspect-square overflow-hidden rounded-md">
      <Skeleton className="h-full w-full rounded-md" />
      <Skeleton className="absolute right-2.5 top-2.5 h-8 w-8 rounded-md bg-white/95" />
      <Skeleton className="absolute bottom-3 left-3 h-4 w-2/3" />
    </div>
  </div>
);
